// Ties a Clarity recording to the devis it belongs to, so that a session can be opened from
// the prospect's file in the admin (NB_Admin, docs/clarity-plateforme.md).
//
// WITHOUT THIS, A RECORDING IS ANONYMOUS. Clarity knows a browser; the admin knows a quote.
// Nothing joins the two unless the page says so — which is the whole of what this module does:
// it names the session, then writes the ids Clarity hands back onto the devis record.
//
// ⚠ NOTHING HAPPENS WITHOUT CONSENT (Loi 25). Clarity itself records in a cookieless mode
// before an answer, which is lawful because nobody is identified. Naming the quote is exactly
// what ends that, so it waits for `accorde` — at load if the answer is already stored, or on
// the click that grants it (shared/components/ConsentBanner).
//
// THE IDS TRAVEL ON THE DEVIS RECORD, not through a call of their own. The record is already
// mirrored to the platform on every change (utils/core/devisSync.js), debounced, retried and
// proxied same-origin. A second path would have to repeat all three and could disagree with
// the first about what the devis holds.

import { onDevisIdentified, readDevis, saveDevis } from '/shared/utils/core/devisRecord.js';
import { currentStep } from '/shared/analytics/funnelStep.js';
import { note } from '/shared/analytics/prospection.js';
import { deviceLabel } from '/shared/analytics/device.js';

const CONSENT_KEY = 'nb-consentement';
/** The steps seen during this visit, for the session we will send. */
const STEPS_KEY = 'nb-etapes';
const GRANTED = 'accorde';

// The devis this page has already named, so a second call changes nothing. Clarity keeps one
// identity per session; repeating it would only queue work.
let named = '';

/**
 * Wire the recording to the devis. Called once at bootstrap, on the funnel pages.
 *
 * @returns {void}
 */
export function initClarity() {
  // The steps are recorded on EVERY page, not only when Clarity answers: consent often
  // comes mid-visit, and the session would then be announced with a single step when the
  // visitor had already crossed three.
  stepsSeen();
  attach();
  // The visitor usually arrives before the quote exists: the id lands mid-funnel, when the
  // lead form answers. This is the moment the admin's doc calls "dès que le devis a un id".
  onDevisIdentified(() => attach());
}

/**
 * Name the session now, because consent has just been granted.
 *
 * Called by the banner rather than watched from here: storage fires no event in the tab that
 * wrote it, so polling would be the only alternative.
 *
 * @returns {void}
 */
export function clarityConsentGranted() {
  attach();
}

/** Identify the session and record its ids, when everything needed is present. */
function attach() {
  // Absent off ninjableu.ca, where the measurement tag's own host guard never fires — a
  // developer machine records nothing and must therefore name nothing.
  if (typeof window.clarity !== 'function') return;
  if (!granted()) return;

  const devis = readDevis();
  if (!devis.id || devis.id === named) return;
  named = devis.id;

  // The quote number is the join key the admin searches on ("Custom user ID"), and the page is
  // where this session started. The company name is only a label, for a human reading the list.
  window.clarity('identify', devis.id, null, location.pathname, devis.company || undefined);
  // The same value as a tag, which is what Clarity filters recordings by.
  window.clarity('set', 'devis', devis.id);

  window.clarity('metadata', (meta) => keep(devis.id, meta));
}

/**
 * Write the session's ids onto the devis, once they are known.
 *
 * @param {string} id the devis these ids belong to
 * @param {{projectId?: string, userId?: string, sessionId?: string}} meta
 */
function keep(id, meta) {
  const projectId = String(meta?.projectId ?? '');
  const userId = String(meta?.userId ?? '');
  const sessionId = String(meta?.sessionId ?? '');
  // All three or none: the admin builds one address out of them, and two thirds of an address
  // is a dead link rather than a missing one.
  if (!projectId || !userId || !sessionId) return;

  const current = readDevis();
  // The visitor may have moved on to another quote while Clarity was answering.
  if (current.id !== id) return;
  // One write per session, not per page: the ids are stable for as long as the recording is.
  if (current.clarity?.sessionId === sessionId) return;

  saveDevis({ clarity: { projectId, userId, sessionId, at: Date.now() } });

  // ⚠ AND SENT TO THE SERVER, which is what was missing: the ids stayed on the devis record,
  // and the admin's Clarity tab said « Aucune séance enregistrée » (reported 2026-10-15).
  // Without this gesture nobody can open the recording at all.
  //
  // ONE SESSION PER VISIT: the guard above only lets a NEW `sessionId` through, and Clarity
  // keeps the same one for as long as the visit lasts. A return days later opens another one,
  // and therefore carries its own session.
  note(
    'session',
    { projectId, userId, sessionId, device: deviceLabel(), pages: stepsSeen() },
    { page: currentStep() }
  );
}

/**
 * The steps this visit has seen so far.
 *
 * ⚠ SO FAR, AND NO FURTHER. The session is sent as soon as Clarity answers, early in the
 * visit: the steps that follow will not be in it. The alternative — waiting for the visit to
 * end — risks sending nothing at all when a tab closes without warning, and a link to the
 * recording is worth more than a complete step list nobody ever receives.
 *
 * @returns {string[]}
 */
function stepsSeen() {
  const step = currentStep();
  try {
    const seen = JSON.parse(sessionStorage.getItem(STEPS_KEY) ?? '[]');
    if (step && !seen.includes(step)) seen.push(step);
    sessionStorage.setItem(STEPS_KEY, JSON.stringify(seen));
    return seen;
  } catch {
    return step ? [step] : [];
  }
}

/** @returns {boolean} whether the visitor has accepted cookies, here or on the public site */
function granted() {
  try {
    return localStorage.getItem(CONSENT_KEY) === GRANTED;
  } catch {
    // Storage refused: the answer cannot be read, so it is not an answer. Measured without a
    // name, like a visitor who has not spoken yet.
    return false;
  }
}
